import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { 
  Send, Bot, User, Target,
  Sparkles, Calendar, MessageSquare, ArrowRight
} from "lucide-react";

const AIChatbot = () => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm Steve's AI Coaching Assistant. I'm here to help you explore your leadership challenges and goals. What's on your mind today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const { user, token } = useAuth();

  const cleanMarkdown = (text) => {
    if (!text) return text;
    return text
      .replace(/#{1,6}\s/g, '')       // ## headings
      .replace(/\*\*(.*?)\*\*/g, '$1') // **bold**
      .replace(/\*(.*?)\*/g, '$1')     // *italic*
      .replace(/`(.*?)`/g, '$1')       // `code`
      .replace(/^[-*]\s/gm, '- ')     // normalize bullets to dashes
      .trim();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      }, { headers });

      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      toast.error("Failed to get response. Please try again.");
      setMessages(prev => prev.slice(0, -1));
    }
    setLoading(false);
  };

  const suggestedQuestions = [
    "How can I improve my executive presence?",
    "What's a good approach to difficult conversations?",
    "How do I build trust with my team?",
    "Tips for managing up effectively?"
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col pt-20" data-testid="ai-chatbot-page">
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-8 flex flex-col">
        <div className="grid lg:grid-cols-3 gap-8 flex-1">
          {/* Chat Area */}
          <div className="lg:col-span-2 flex flex-col">
            <Card className="flex-1 flex flex-col">
              <CardHeader className="border-b border-stone-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">AI Coaching Assistant</CardTitle>
                    <p className="text-sm text-slate-500">Powered by Steve's coaching philosophy</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-0">
                <ScrollArea className="flex-1 p-6" style={{ height: "400px" }}>
                  <div className="space-y-6">
                    {messages.map((msg, index) => (
                      <div 
                        key={index} 
                        className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                        data-testid={`chat-message-${index}`}
                      >
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          {msg.role === "assistant" ? (
                            <>
                              <AvatarImage src="https://customer-assets-jt897jd0.emergentagent.net/job_mountain-executive/artifacts/j0767y4t_Screenshot%202026-07-23%205.03.57%20PM.png" />
                              <AvatarFallback className="bg-orange-100 text-orange-600">AI</AvatarFallback>
                            </>
                          ) : (
                            <AvatarFallback className="bg-slate-100 text-slate-600">
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div 
                          className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                            msg.role === "user" 
                              ? "chat-bubble-user text-white" 
                              : "chat-bubble-ai text-slate-800"
                          }`}
                        >
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{cleanMarkdown(msg.content)}</p>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="https://customer-assets-jt897jd0.emergentagent.net/job_mountain-executive/artifacts/j0767y4t_Screenshot%202026-07-23%205.03.57%20PM.png" />
                          <AvatarFallback className="bg-orange-100 text-orange-600">AI</AvatarFallback>
                        </Avatar>
                        <div className="chat-bubble-ai rounded-2xl px-4 py-3">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input area */}
                <div className="border-t border-stone-100 p-4">
                  <form onSubmit={handleSubmit} className="flex gap-2">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Type your message..."
                      disabled={loading}
                      className="flex-1 h-12"
                      data-testid="chat-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={loading || !input.trim()}
                      className="h-12 px-6 bg-orange-600 hover:bg-orange-500"
                      data-testid="chat-submit"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick questions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-orange-600" />
                  Try asking...
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="w-full text-left text-sm p-3 rounded-lg bg-stone-50 hover:bg-stone-100 text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid={`suggested-question-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* About the AI */}
            <Card className="bg-slate-800 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Bot className="h-6 w-6 text-orange-400" />
                  <h3 className="font-semibold">About This AI</h3>
                </div>
                <p className="text-sm text-white/80 leading-relaxed mb-4">
                  This AI assistant is trained on Steve's coaching philosophy and frameworks. 
                  While it can provide initial guidance, it's designed to complement—not 
                  replace—personalized coaching sessions.
                </p>
                <Link to="/booking">
                  <Button variant="secondary" size="sm" className="w-full">
                    Book a Real Session
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Leadership Quiz CTA */}
            <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-stone-50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center mb-4">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">How Strong Is Your Leadership?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Take our free 3-minute assessment and discover your strengths across 5 key leadership dimensions.
                </p>
                <Link to="/quiz">
                  <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-500" data-testid="chat-quiz-cta">
                    Take the Free Quiz
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Not logged in prompt */}
            {!user && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-6">
                  <MessageSquare className="h-6 w-6 text-orange-600 mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-2">Save Your Conversations</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Create a free account to save your chat history and track your progress.
                  </p>
                  <Link to="/register">
                    <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-500">
                      Create Free Account
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatbot;
